import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/hooks/useOrganization";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { TrendingUp, TrendingDown, Minus, Users, CalendarCheck, Percent, Download } from "lucide-react";
import { format } from "date-fns";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

interface Props {
  initialSeasonId?: string | null;
}

export default function AdminAttendanceStats({ initialSeasonId }: Props) {
  const { t } = useTranslation("admin");
  const { organization } = useOrganization();
  const [seasonId, setSeasonId] = useState(initialSeasonId || "");
  const [groupId, setGroupId] = useState("");

  const { data: seasons = [] } = useQuery({
    queryKey: ["seasons-stats", organization?.id],
    enabled: !!organization,
    queryFn: async () => {
      const { data } = await supabase
        .from("seasons")
        .select("id, name, start_date, end_date")
        .eq("organization_id", organization!.id)
        .order("start_date", { ascending: false });
      return data ?? [];
    },
  });

  const { data: groups = [] } = useQuery({
    queryKey: ["groups-stats", seasonId],
    enabled: !!seasonId,
    queryFn: async () => {
      const { data } = await supabase
        .from("training_groups")
        .select("id, name, color")
        .eq("season_id", seasonId)
        .order("name");
      return data ?? [];
    },
  });

  const { data: rawAttendance = [] } = useQuery({
    queryKey: ["attendance-stats", groupId],
    enabled: !!groupId,
    queryFn: async () => {
      const { data } = await supabase
        .from("session_attendance")
        .select(
          "actual_status, player_id, session_id, profiles!session_attendance_player_id_fkey(full_name), training_sessions!inner(session_date, group_id, status)"
        )
        .eq("training_sessions.group_id", groupId)
        .eq("training_sessions.status", "completed");
      return data ?? [];
    },
  });

  // Auto-select first season/group
  if (!seasonId && seasons.length > 0) {
    setTimeout(() => setSeasonId(seasons[0].id), 0);
  }
  if (!groupId && groups.length > 0) {
    setTimeout(() => setGroupId(groups[0].id), 0);
  }

  const stats = useMemo(() => {
    if (rawAttendance.length === 0) return null;

    // Group by session for session-level stats
    const sessionMap = new Map<string, { date: string; records: any[] }>();
    const playerMap = new Map<string, { name: string; records: { status: string; date: string }[] }>();

    rawAttendance.forEach((a: any) => {
      const sid = a.session_id;
      const date = a.training_sessions?.session_date ?? "";
      if (!sessionMap.has(sid)) sessionMap.set(sid, { date, records: [] });
      sessionMap.get(sid)!.records.push(a);

      const pid = a.player_id;
      if (!playerMap.has(pid)) playerMap.set(pid, { name: a.profiles?.full_name || t("training.unknown"), records: [] });
      playerMap.get(pid)!.records.push({ status: a.actual_status || "present", date });
    });

    const totalSessions = sessionMap.size;
    const totalPlayers = playerMap.size;

    // Per-player stats
    const playerStats = Array.from(playerMap.entries()).map(([pid, { name, records }]) => {
      const sorted = [...records].sort((a, b) => a.date.localeCompare(b.date));
      const total = sorted.length;
      const present = sorted.filter((r) => r.status === "present").length;
      const late = sorted.filter((r) => r.status === "late").length;
      const absent = sorted.filter((r) => r.status === "absent").length;
      const noShow = sorted.filter((r) => r.status === "no_show").length;
      const attendPct = total > 0 ? Math.round(((present + late) / total) * 100) : 0;

      // Streak
      let streak = 0;
      for (let i = sorted.length - 1; i >= 0; i--) {
        if (sorted[i].status === "present" || sorted[i].status === "late") streak++;
        else break;
      }

      // Trend: last 5 vs total
      const last5 = sorted.slice(-5);
      const last5Pct = last5.length > 0 ? (last5.filter((r) => r.status === "present" || r.status === "late").length / last5.length) * 100 : 0;
      const trend = last5Pct > attendPct + 5 ? "up" : last5Pct < attendPct - 5 ? "down" : "neutral";

      return { pid, name, total, present, late, absent, noShow, attendPct, streak, trend };
    });

    playerStats.sort((a, b) => b.attendPct - a.attendPct);

    // Overall average
    const avgAttendance = playerStats.length > 0 ? Math.round(playerStats.reduce((s, p) => s + p.attendPct, 0) / playerStats.length) : 0;

    // Trend chart: per session average attendance %
    const sessionTrend = Array.from(sessionMap.entries())
      .sort(([, a], [, b]) => a.date.localeCompare(b.date))
      .map(([, { date, records }]) => {
        const att = records.filter((r: any) => r.actual_status === "present" || r.actual_status === "late").length;
        return { date, pct: Math.round((att / records.length) * 100) };
      });

    return { totalSessions, totalPlayers, avgAttendance, playerStats, sessionTrend };
  }, [rawAttendance]);

  const selectedGroup = groups.find((g: any) => g.id === groupId);

  const exportCSV = () => {
    if (!stats) return;
    const header = `${t("training.player")};${t("training.sessions")};${t("training.attendanceShort")};${t("training.lateShort")};${t("training.absentShort")};${t("training.noShowShort")};${t("training.percent")}\n`;
    const rows = stats.playerStats
      .map((p) => `${p.name};${p.total};${p.present};${p.late};${p.absent};${p.noShow};${p.attendPct}%`)
      .join("\n");
    const blob = new Blob(["\uFEFF" + header + rows], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `narvaro_${(selectedGroup?.name || "grupp").replace(/\s+/g, "_")}_${format(new Date(), "yyyy-MM-dd")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-3 flex-wrap items-center">
        <Select value={seasonId} onValueChange={(v) => { setSeasonId(v); setGroupId(""); }}>
          <SelectTrigger className="w-[200px]"><SelectValue placeholder={t("training.selectSeason")} /></SelectTrigger>
          <SelectContent>
            {seasons.map((s: any) => (
              <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={groupId} onValueChange={setGroupId}>
          <SelectTrigger className="w-[200px]"><SelectValue placeholder={t("training.selectGroup")} /></SelectTrigger>
          <SelectContent>
            {groups.map((g: any) => (
              <SelectItem key={g.id} value={g.id}>
                <span className="flex items-center gap-2">
                  {g.color && <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ backgroundColor: g.color }} />}
                  {g.name}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {stats && (
          <Button size="sm" variant="outline" onClick={exportCSV} className="gap-1.5">
            <Download className="h-3.5 w-3.5" /> {t("training.exportCSV")}
          </Button>
        )}
      </div>

      {!groupId && <p className="text-sm text-muted-foreground">{t("training.selectSeasonAndGroup")}</p>}

      {stats && (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-3 gap-3">
            <Card>
              <CardContent className="py-3 px-4 flex items-center gap-3">
                <CalendarCheck className="h-5 w-5 text-primary" />
                <div>
                  <p className="text-2xl font-bold">{stats.totalSessions}</p>
                  <p className="text-xs text-muted-foreground">{t("training.completedSessions")}</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="py-3 px-4 flex items-center gap-3">
                <Percent className="h-5 w-5 text-primary" />
                <div>
                  <p className="text-2xl font-bold">{stats.avgAttendance}%</p>
                  <p className="text-xs text-muted-foreground">{t("training.avgAttendance")}</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="py-3 px-4 flex items-center gap-3">
                <Users className="h-5 w-5 text-primary" />
                <div>
                  <p className="text-2xl font-bold">{stats.totalPlayers}</p>
                  <p className="text-xs text-muted-foreground">{t("training.players")}</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Trend chart */}
          {stats.sessionTrend.length > 1 && (
            <Card>
              <CardContent className="py-4 px-4">
                <h3 className="text-sm font-semibold mb-3">{t("training.attendanceTrend")}</h3>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={stats.sessionTrend}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 10 }}
                      tickFormatter={(v) => {
                        const d = new Date(v);
                        return `${d.getDate()}/${d.getMonth() + 1}`;
                      }}
                    />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} unit="%" />
                    <Tooltip
                      formatter={(value: number) => [`${value}%`, t("training.attendance")]}
                      labelFormatter={(v) => {
                        const d = new Date(v);
                        return `${d.getDate()}/${d.getMonth() + 1}`;
                      }}
                    />
                    <Line type="monotone" dataKey="pct" className="stroke-primary" strokeWidth={2} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {/* Player table */}
          <Card>
            <CardContent className="py-3 px-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("training.player")}</TableHead>
                      <TableHead className="text-center">{t("training.sessions")}</TableHead>
                      <TableHead className="text-center">{t("training.attendanceShort")}</TableHead>
                      <TableHead className="text-center">{t("training.lateShort")}</TableHead>
                      <TableHead className="text-center">{t("training.absentShort")}</TableHead>
                      <TableHead className="text-center">{t("training.noShowShort")}</TableHead>
                      <TableHead className="text-center">%</TableHead>
                      <TableHead className="text-center">{t("training.streak")}</TableHead>
                      <TableHead className="text-center">{t("training.trend")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {stats.playerStats.map((p) => (
                      <TableRow key={p.pid}>
                        <TableCell className="font-medium text-sm">{p.name}</TableCell>
                        <TableCell className="text-center text-sm">{p.total}</TableCell>
                        <TableCell className="text-center text-sm">{p.present}</TableCell>
                        <TableCell className="text-center text-sm">{p.late}</TableCell>
                        <TableCell className="text-center text-sm">{p.absent}</TableCell>
                        <TableCell className="text-center text-sm">{p.noShow}</TableCell>
                        <TableCell className="text-center">
                          <Badge
                            variant={p.attendPct >= 80 ? "default" : p.attendPct >= 50 ? "secondary" : "destructive"}
                            className="text-xs"
                          >
                            {p.attendPct}%
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center text-sm">{p.streak}</TableCell>
                        <TableCell className="text-center">
                          {p.trend === "up" && <TrendingUp className="h-4 w-4 text-green-500 mx-auto" />}
                          {p.trend === "down" && <TrendingDown className="h-4 w-4 text-red-500 mx-auto" />}
                          {p.trend === "neutral" && <Minus className="h-4 w-4 text-muted-foreground mx-auto" />}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {groupId && rawAttendance.length === 0 && (
        <p className="text-sm text-muted-foreground">{t("training.noAttendanceData")}</p>
      )}
    </div>
  );
}
